// kitchen/shared/kds/grouping.ts
//
// Feature 252 — grouping identical dishes on one order line.
//
// Grouping is PRESENTATION. Every portion stays its own record; this module only
// decides which portions may be drawn together, and counts them. Nothing here
// writes, merges or discards a portion.
//
// ⚠️ MIRROR: kitchen/expo-app resolves "@shared/*" to its OWN tree, so this file
// has a hand-kept twin at kitchen/expo-app/shared/kds/grouping.ts. The two must
// stay identical; a test compares the keys they produce. The neighbouring
// table-board library has already drifted for want of that test.

/** Everything about a portion that decides whether it groups with another. */
export interface GroupingAttributes {
  dishId: string | null;
  variantId: string | null;
  notes: string | null;
  modifications: string | null;
  isGlutenFreeVersion: boolean;
  courseNumber: number;
  serviceWave: number;
  subAccount: string | null;
  seatNumber: number | null;
  isComped: boolean;
  offMenuName: string | null;
  offMenuUnitPriceCents: number | null;
  fixedMenuAssignmentId: string | null;
  /**
   * Feature 270 — the price a cashier typed on the bill, and why.
   *
   * Both belong in the key. Two portions of one dish at two prices must never
   * be drawn, printed or transmitted as one line: collapsing them would show
   * "2× Gamberoni" at whichever price happened to be read first. And two at
   * the same amount for different reasons are two different explanations on
   * the bill, so they stay apart too.
   */
  priceOverrideCents: number | null;
  priceOverrideReason: string | null;
}

/**
 * Separates the components of a key. A unit separator cannot appear in a dish
 * name, a note or an id, so no value can forge a different key by containing it.
 */
const KEY_SEPARATOR = '\u001f';

/** The order a kitchen line walks through, least advanced first. */
const STATUS_ORDER = [
  'PENDING',
  'CONFIRMED',
  'PREPARING',
  'READY',
  'SERVED',
  'CANCELLED',
] as const;

/**
 * Text components compare trimmed, with null, undefined and "" all one value:
 * two plates that both carry no note match on that attribute, and a plate with
 * a note never joins one without (FR-006).
 *
 * Case is NOT folded. A cook who typed a capital may have meant something.
 */
function text(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function flag(value: boolean | null | undefined): string {
  return value === true ? '1' : '0';
}

function num(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * A stable string standing for the attributes above. Two portions group when,
 * and only when, their keys are equal.
 *
 * Opaque by contract: callers compare keys, they never parse them. The unit
 * separator keeps a value containing the separator from forging another key.
 */
export function groupingKey(a: GroupingAttributes): string {
  return [
    text(a.dishId),
    text(a.variantId),
    text(a.notes),
    text(a.modifications),
    flag(a.isGlutenFreeVersion),
    num(a.courseNumber),
    num(a.serviceWave),
    text(a.subAccount),
    num(a.seatNumber),
    flag(a.isComped),
    text(a.offMenuName),
    num(a.offMenuUnitPriceCents),
    text(a.fixedMenuAssignmentId),
    num(a.priceOverrideCents),
    text(a.priceOverrideReason),
  ].join(KEY_SEPARATOR);
}

export interface GroupedLine<T> {
  /** The key the portions share. Opaque. */
  key: string;
  /** The portions themselves, in the order they appeared. */
  portions: T[];
  /** How many portions — what the cook reads. */
  count: number;
}

/**
 * Groups portions that share a key, preserving the order in which the first
 * portion of each group appeared.
 *
 * With `enabled` false it returns one group per portion, in the original order,
 * which is what keeps a venue that never touched the switch indistinguishable
 * from today (FR-003).
 *
 * The result does not depend on the order the portions arrive in: the same set
 * always yields the same groups with the same counts, which is what makes two
 * people adding a portion at the same moment harmless (FR-008).
 */
export function groupPortions<T>(
  portions: readonly T[],
  read: (portion: T) => GroupingAttributes,
  options: { enabled: boolean },
): GroupedLine<T>[] {
  if (!options.enabled) {
    return portions.map((portion) => ({
      key: groupingKey(read(portion)),
      portions: [portion],
      count: 1,
    }));
  }

  const byKey = new Map<string, GroupedLine<T>>();
  const order: string[] = [];

  for (const portion of portions) {
    const key = groupingKey(read(portion));
    const existing = byKey.get(key);
    if (existing) {
      existing.portions.push(portion);
      existing.count += 1;
    } else {
      byKey.set(key, { key, portions: [portion], count: 1 });
      order.push(key);
    }
  }

  return order.map((key) => byKey.get(key)!);
}

/**
 * Reads the grouping attributes off an order item as the kitchen payload
 * delivers it. Kept here, beside the key, so the surfaces cannot each invent
 * their own reading of the same row.
 *
 * The payload uses `include` on items, so every scalar column is present; a
 * field read as undefined here means the shape changed, and the null-equals-
 * empty rule makes that fail safe by keeping portions apart rather than
 * merging them wrongly.
 */
export function itemGroupingAttributes(item: any): GroupingAttributes {
  return {
    dishId: item?.dishId ?? null,
    variantId: item?.variantId ?? null,
    notes: item?.notes ?? null,
    modifications: item?.modifications ?? null,
    isGlutenFreeVersion: item?.isGlutenFreeVersion === true,
    courseNumber: item?.courseNumber ?? 1,
    serviceWave: item?.serviceWave ?? 1,
    subAccount: item?.subAccount ?? null,
    seatNumber: item?.seatNumber ?? null,
    isComped: item?.isComped === true,
    offMenuName: item?.offMenuName ?? null,
    offMenuUnitPriceCents: item?.offMenuUnitPriceCents ?? null,
    fixedMenuAssignmentId: item?.fixedMenuAssignmentId ?? null,
    priceOverrideCents: item?.priceOverrideCents ?? null,
    priceOverrideReason: item?.priceOverrideReason ?? null,
  };
}

/** Groups order items straight off the kitchen payload. */
export function groupKdsItems(
  items: readonly any[],
  enabled: boolean,
): GroupedLine<any>[] {
  return groupPortions(items, itemGroupingAttributes, { enabled });
}

export interface GroupedLineStatus {
  /** The least advanced status among the portions. */
  status: string;
  /** How many portions sit at each status. */
  counts: Record<string, number>;
}

/**
 * The status a collapsed line shows.
 *
 * The LEAST advanced status of the portions it holds, so a line can never claim
 * to be further along than its slowest plate: three portions of which two are
 * ready still read as in preparation. The per-status counts travel with it, so
 * the collapsed line can say "two of three done" without being expanded.
 *
 * Cancelled portions are not counted towards the line's progress — a line of
 * three with one cancelled is a line of two being cooked, not a cancelled line.
 * They stay in `counts` so nothing disappears silently.
 */
export function groupedLineStatus(
  statuses: readonly string[],
): GroupedLineStatus {
  const counts: Record<string, number> = {};
  for (const status of statuses) {
    counts[status] = (counts[status] ?? 0) + 1;
  }

  const live = statuses.filter((s) => s !== 'CANCELLED');
  if (live.length === 0) {
    return { status: statuses.length > 0 ? 'CANCELLED' : 'PENDING', counts };
  }

  let least = live[0];
  for (const status of live) {
    if (rank(status) < rank(least)) least = status;
  }
  return { status: least, counts };
}

/** Unknown statuses sort last rather than throwing: a board must still draw. */
function rank(status: string): number {
  const index = (STATUS_ORDER as readonly string[]).indexOf(status);
  return index === -1 ? STATUS_ORDER.length : index;
}
